import { Application, Container, Graphics, Sprite, UPDATE_PRIORITY } from 'pixi.js';
import './style.css';
import { InputManager } from '@/core/input';
import { FixedTimestep, interpolate } from '@/core/loop';
import { PerfMonitor, formatPerf } from '@/core/perf';
import {
  CURRENT_SAVE_VERSION,
  DEFAULT_START_MINUTES,
  randomSeed,
  readSave,
  writeSave,
} from '@/core/save';
import { RecordBook } from '@/core/records';
import { loadSettings, saveSettings } from '@/core/settings';
import {
  VIRTUAL_HEIGHT,
  VIRTUAL_WIDTH,
  type ViewName,
  computeViewport,
  fitViewTo,
  setView,
  shouldPromptRotate,
  viewHeight,
  viewWidth,
} from '@/core/viewport';
import { isAreaId } from '@/data/maps';
import { ambientFor } from '@/render/ambient';
import { FarmlandRenderer } from '@/render/farmlandRenderer';
import { PropsRenderer } from '@/render/propsRenderer';
import { Camera } from '@/render/camera';
import { bakeAll, bakeTexture } from '@/render/spriteBaker';
import { GroundItemsRenderer } from '@/render/groundItemsRenderer';
import { TileCursor } from '@/render/tileCursor';
import { TilemapRenderer } from '@/render/tilemapRenderer';
import { WeatherRenderer } from '@/render/weatherRenderer';
import { CROP_SPRITES, type CropSpriteKey } from '@/sprites/crops';
import { FURNITURE_SPRITES, type FurnitureSpriteKey } from '@/sprites/furniture';
import { KATIE_SPRITES, type KatieSpriteKey, spriteKeyFor } from '@/sprites/katie';
import {
  HELD_TOOL_SPRITES,
  type HeldToolSpriteKey,
  heldToolSpriteKey,
} from '@/sprites/toolsHeld';
import { Swing, overlaps } from '@/combat/swing';
import { REST_POSE, swingPose } from '@/render/swingPose';
import { Helper, homeTimeFor, summonPosition } from '@/combat/helper';
import { EnemyForce } from '@/combat/enemies';
import { beginAttack } from '@/combat/attack';
import { Combo } from '@/combat/combo';
import { Dodge } from '@/combat/dodge';
import { Projectiles } from '@/combat/projectiles';
import { Spirit } from '@/combat/spirit';
import { BOLT_SPRITE } from '@/sprites/bolt';
import { Health } from '@/combat/health';
import { AREA_SPAWN_RULES } from '@/data/enemies';
import { type WeaponId, isWeaponId } from '@/data/weapons';
import { SPIRIT_PER_HIT } from '@/data/abilities';
import { SpawnDirector, isNightHour } from '@/combat/spawnDirector';
import { MAX_DEPTH } from '@/dungeon/area';
import { Chests } from '@/dungeon/chests';
import { TrapField } from '@/dungeon/traps';
import { Boss } from '@/combat/boss';
import { arenaCentre, isBossDepth } from '@/dungeon/arena';
import { bossFor } from '@/data/bosses';
import { BOSS_SPRITES, type BossSpriteKey } from '@/sprites/boss';
import { TrapRenderer } from '@/render/trapRenderer';
import { TRAP_SPRITES, type TrapSpriteKey } from '@/sprites/traps';
import { generateFloor } from '@/dungeon/generator';
import { ChestRenderer } from '@/render/chestRenderer';
import { CHEST_SPRITES, type ChestSpriteKey } from '@/sprites/chest';
import { isTool } from '@/world/toolbelt';
import { DamageNumbers } from '@/render/damageNumbers';
import { EnemyRenderer } from '@/render/enemyRenderer';
import { DIGIT_SPRITES, type DigitSpriteKey } from '@/sprites/digits';
import { ENEMY_SPRITES, type EnemySpriteKey } from '@/sprites/enemies';
import { HealthBar } from '@/ui/healthBar';
import { SLASH_SPRITES, SLASH_FRAMES, type SlashSpriteKey } from '@/sprites/slash';
import { GROUND_ITEM_SPRITE } from '@/sprites/groundItem';
import { TILE_SPRITES, type TileSpriteKey } from '@/sprites/tiles';
import { installGestureGuards } from '@/ui/gestureGuards';
import { CatchPopup } from '@/ui/catchPopup';
import { GearPanel } from '@/ui/gearPanel';
import { ShopPanel } from '@/ui/shopPanel';
import { RequestBoardPanel } from '@/ui/requestBoardPanel';
import { DialoguePanel } from '@/ui/dialoguePanel';
import { NpcCrew } from '@/world/npcs';
import { NPCS, type NpcDefinition, type NpcId } from '@/data/npcs';
import { questsFrom, type QuestId } from '@/data/quests';
import { type Festival, festivalOn } from '@/data/festivals';
import { NpcRenderer } from '@/render/npcRenderer';
import { NPC_SPRITES, type NpcSpriteKey } from '@/sprites/npcs';
import { FishingUi } from '@/ui/fishingUi';
import { Hud } from '@/ui/hud';
import { ItemBar } from '@/ui/itemBar';
import { Toast } from '@/ui/toast';
import { SettingsPanel } from '@/ui/settingsPanel';
import { ToolButton } from '@/ui/toolButton';
import { TouchControls } from '@/ui/touchControls';
import type { FishId } from '@/data/fish';
import { ITEMS, fishItem } from '@/data/items';
import { isPlaceable, useHeldItem } from '@/world/farmActions';
import { describePickup, pickUpAt } from '@/world/pickup';
import { FishingSession, describeResult } from '@/world/fishing';
import { weightFor } from '@/data/fish';
import { type ItemId, baitOf } from '@/data/items';
import { BAITS, RODS, isRodId } from '@/data/tackle';
import { Farmland } from '@/world/farmland';
import { Inventory } from '@/world/inventory';
import { QuickBag } from '@/world/quickBag';
import { Session } from '@/world/session';

/**
 * Stands in for the boss in a swing's memory of what it has already hit.
 *
 * The set holds entity ids and the boss is not an entity — it is one thing,
 * not a store. A reserved id keeps "one hit per swing" true for it without
 * giving it an entity it would otherwise have no use for.
 */
const BOSS_HIT_ID = -1;

const AUTOSAVE_INTERVAL_MS = 30_000;
/** Half of a door transition: fade out, swap, fade back in. */
const FADE_SECONDS = 0.18;

const canvas = requireElement<HTMLCanvasElement>('#stage');
const uiLayer = requireElement<HTMLDivElement>('#ui');
const rotatePrompt = requireElement<HTMLDivElement>('#rotate-prompt');

async function boot(): Promise<() => void> {
  const lifetime = new AbortController();
  const { signal } = lifetime;
  const disposers: (() => void)[] = [];

  const app = new Application();
  await app.init({
    canvas,
    width: VIRTUAL_WIDTH,
    height: VIRTUAL_HEIGHT,
    background: '#1b2b34',
    antialias: false,
    roundPixels: true,
    autoDensity: false,
    preference: 'webgl',
  });

  // The view is a device preference, read before the first frame so the world
  // is never drawn at one size and then snapped to another.
  setView(loadSettings().view);

  applyViewport(app);
  window.addEventListener('resize', () => { applyViewport(app); }, { signal });
  window.addEventListener('orientationchange', () => { applyViewport(app); }, { signal });

  const tileTextures = bakeAll<TileSpriteKey>(TILE_SPRITES);
  const katieTextures = bakeAll<KatieSpriteKey>(KATIE_SPRITES);
  const cropTextures = bakeAll<CropSpriteKey>(CROP_SPRITES);
  const propTextures = bakeAll<FurnitureSpriteKey>(FURNITURE_SPRITES);
  const heldToolTextures = bakeAll<HeldToolSpriteKey>(HELD_TOOL_SPRITES);
  const groundItemTexture = bakeTexture(GROUND_ITEM_SPRITE);
  const slashTextures = bakeAll<SlashSpriteKey>(SLASH_SPRITES);
  const enemyTextures = bakeAll<EnemySpriteKey>(ENEMY_SPRITES);
  const digitTextures = bakeAll<DigitSpriteKey>(DIGIT_SPRITES);
  const boltTexture = bakeTexture(BOLT_SPRITE);
  const chestTextures = bakeAll<ChestSpriteKey>(CHEST_SPRITES);
  const npcTextures = bakeAll<NpcSpriteKey>(NPC_SPRITES);
  const trapTextures = bakeAll<TrapSpriteKey>(TRAP_SPRITES);
  const bossTextures = bakeAll<BossSpriteKey>(BOSS_SPRITES);

  // ?seed=123 forces the world seed, which is otherwise random on a new farm
  // and then saved. Weather, fish and dungeon floors all derive from it, so
  // pinning it makes "the same world" reproducible on demand — and it is the
  // only way to test that something *else* is what varies.
  const forcedSeed = Number(new URLSearchParams(window.location.search).get('seed'));
  const hasForcedSeed = Number.isFinite(forcedSeed) && forcedSeed !== 0;

  const saved = await readSave();
  const session = new Session({
    seed: hasForcedSeed ? forcedSeed : (saved?.seed ?? randomSeed()),
    ...(saved && isAreaId(saved.map) ? { areaId: saved.map } : {}),
    // A depth names a floor exactly — it is rebuilt from (seed, depth) — so a
    // save written underground restores the descent rather than resurfacing.
    ...(saved && saved.depth > 0
      ? { depth: saved.depth, surfaceReturn: saved.surfaceReturn }
      : {}),
    // Position travels with a known area or with a depth. It must not travel
    // with neither: an unrecognised area falls back to the farm, and the tile
    // Katie stood on three floors down would put her inside a fence.
    ...(saved && (isAreaId(saved.map) || saved.depth > 0)
      ? { playerX: saved.player.x, playerY: saved.player.y }
      : {}),
    totalMinutes: saved?.totalMinutes ?? DEFAULT_START_MINUTES,
    farmland: saved ? Farmland.fromSave(saved.farmland) : new Farmland(),
    inventory: saved ? Inventory.fromSave(saved.inventory) : Inventory.starting(),
    quickBag: saved
      ? QuickBag.fromSave(saved.quickBag, saved.selectedSlot)
      : QuickBag.starting(),
    ...(saved
      ? {
          livestock: saved.livestock,
          furniture: saved.furniture,
          belt: saved.belt,
          ground: saved.ground,
          weaponGrowth: saved.weaponGrowth,
          weaponAbilities: saved.weaponAbilities,
          coins: saved.coins,
          friendships: saved.friendships,
          requests: saved.requests,
          quests: saved.quests,
          festivals: saved.festivals,
        }
      : {}),
  });
  if (saved) {
    session.player.facing = saved.player.facing as typeof session.player.facing;
  }

  // ?area=cottage&at=x,y drops Katie somewhere specific. Walking across the
  // farm to look at one fence is a slow way to look at a fence, and it makes a
  // bug reproducible in a link: "open ?area=cottage&at=4,4 and press Use".
  const params = new URLSearchParams(window.location.search);
  const startArea = params.get('area');
  if (startArea && isAreaId(startArea)) session.enterArea(startArea);

  const at = params.get('at');
  if (at) {
    const [tileX, tileY] = at.split(',').map(Number);
    if (Number.isFinite(tileX) && Number.isFinite(tileY)) {
      session.player.x = tileX! * 16;
      session.player.y = tileY! * 16;
    }
  }

  // Scene graph: tiles, then Katie, then the ambient tint, then weather. The
  // tint sits under weather so rain is not dimmed along with the world.
  const world = new Container();
  const katie = new Sprite(katieTextures['down-idle']);
  // Two sprites rather than one that gets reparented: facing changes every few
  // frames while walking, and swapping a child's index that often is churn for
  // something a visibility flag settles. Whichever side of Katie the tool
  // belongs on that frame is the one holding the texture.
  const heldToolBehind = new Sprite();
  const heldToolFront = new Sprite();
  heldToolBehind.visible = false;
  heldToolFront.visible = false;
  // Rotated by quarter turns per facing, so its origin is its middle.
  const slash = new Sprite();
  slash.anchor.set(0.5);
  slash.visible = false;
  const bossSprite = new Sprite();
  bossSprite.anchor.set(0.5);
  bossSprite.visible = false;
  let tilemapRenderer = new TilemapRenderer(session.area.map, tileTextures);
  // Soil and crops sit between the ground and Katie, so she walks over the rows.
  const farmlandRenderer = new FarmlandRenderer(session.farmland, tileTextures, cropTextures);
  const propsRenderer = new PropsRenderer(propTextures);
  const enemyRenderer = new EnemyRenderer(enemyTextures);
  disposers.push(() => { enemyRenderer.destroy(); });
  const damageNumbers = new DamageNumbers(digitTextures);
  disposers.push(() => { damageNumbers.destroy(); });
  const boltSprites: Sprite[] = [];
  const boltLayer = new Container();
  const npcs = new NpcCrew();
  const npcRenderer = new NpcRenderer(npcTextures);
  disposers.push(() => { npcRenderer.destroy(); });
  const trapRenderer = new TrapRenderer(trapTextures);
  disposers.push(() => { trapRenderer.destroy(); });
  const chestRenderer = new ChestRenderer(chestTextures);
  disposers.push(() => { chestRenderer.destroy(); });
  /** Today's festival, if today is one. Derived from the date, like the weather. */
  const festivalToday = (): Festival | null =>
    festivalOn(session.clock.season, session.clock.dayOfSeason);

  const helper = new Helper();
  // Her own sprite rather than a second NpcRenderer: the schedule renderer
  // draws people who are where the hour says, and she has left her post.
  const helperSprite = new Sprite();
  helperSprite.visible = false;

  const groundItemsRenderer = new GroundItemsRenderer(groundItemTexture);
  disposers.push(() => { groundItemsRenderer.destroy(); });
  // One definition of the layer order, used at boot and on every area change.
  // It was duplicated, which is how a newly added sprite ends up layered
  // correctly on the farm and wrongly the moment you walk through a door.
  // `tilemapRenderer` is reassigned per area, so this reads it at call time.
  const layerWorld = (): void => {
    world.removeChildren();
    world.addChild(
      tilemapRenderer.container,
      farmlandRenderer.container,
      propsRenderer.container,
      trapRenderer.container,
      groundItemsRenderer.container,
      bossSprite,
      chestRenderer.container,
      npcRenderer.container,
      helperSprite,
      enemyRenderer.container,
      boltLayer,
      heldToolBehind,
      katie,
      heldToolFront,
      slash,
      damageNumbers.container,
    );
  };
  layerWorld();

  // Both overlays cover the widest view, so changing to a closer one leaves
  // them oversized rather than short — a fade with a gap down one side is the
  // one thing they must never do.
  const ambientOverlay = new Graphics().rect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT).fill(0xffffff);
  ambientOverlay.alpha = 0;

  const weatherRenderer = new WeatherRenderer();

  const fadeOverlay = new Graphics().rect(0, 0, VIRTUAL_WIDTH, VIRTUAL_HEIGHT).fill(0x000000);
  fadeOverlay.alpha = 0;

  // The cursor sits above the ambient tint so it stays readable after dark,
  // and below weather so rain still falls in front of the world.
  const tileCursor = new TileCursor();
  disposers.push(() => { tileCursor.destroy(); });

  app.stage.addChild(
    world,
    ambientOverlay,
    tileCursor.container,
    weatherRenderer.container,
    fadeOverlay,
  );

  const camera = new Camera();
  camera.snapTo(session.player.centreX, session.player.centreY, session.area.bounds);

  const input = new InputManager();
  input.attach();
  disposers.push(() => { input.detach(); });

  try {
    disposers.push(installGestureGuards({ root: requireElement<HTMLElement>('#app') }));
  } catch (error) {
    console.warn('Gesture guards unavailable.', error);
  }

  const healthBar = new HealthBar(uiLayer);
  disposers.push(() => { healthBar.destroy(); });

  const bossBar = document.createElement('div');
  bossBar.className = 'boss-bar';
  bossBar.hidden = true;
  const bossFill = document.createElement('div');
  bossFill.className = 'boss-bar-fill';
  const bossName = document.createElement('span');
  bossName.className = 'boss-bar-name';
  bossBar.append(bossFill, bossName);
  uiLayer.appendChild(bossBar);
  disposers.push(() => { bossBar.remove(); });

  const spiritBar = document.createElement('div');
  spiritBar.className = 'spirit-bar';
  spiritBar.setAttribute('aria-hidden', 'true');
  uiLayer.appendChild(spiritBar);
  disposers.push(() => { spiritBar.remove(); });

  const hud = new Hud(uiLayer);
  disposers.push(() => { hud.destroy(); });

  const toast = new Toast(uiLayer);
  disposers.push(() => { toast.destroy(); });

  const itemBar = new ItemBar(uiLayer, {
    onSelect: (index) => { session.quickBag.select(index); },
    onAssign: (index, id) => {
      session.quickBag.assign(index, id);
      session.quickBag.select(index);
      save();
    },
  });
  disposers.push(() => { itemBar.destroy(); });

  const fishingUi = new FishingUi(uiLayer);
  disposers.push(() => { fishingUi.destroy(); });

  const catchPopup = new CatchPopup(uiLayer);
  disposers.push(() => { catchPopup.destroy(); });

  // The record book outlives any single playthrough, so it is not part of the
  // save; see src/core/records.ts.
  const records = new RecordBook();

  const gearPanel = new GearPanel({
    container: uiLayer,
    inventory: session.inventory,
    quickBag: session.quickBag,
    weaponGrowth: session.weaponGrowth,
    weaponAbilities: session.weaponAbilities,
    belt: session.belt,
    unlocked: () => session.quests.unlockedRecipes(),
    getBait: () => session.equippedBait as ItemId | null,
    onChooseBait: (bait: ItemId | null) => {
      session.equippedBait = bait;
      save();
    },
    onChanged: () => {
      save();
    },
    onMessage: (message: string) => {
      toast.show(message);
    },
    // Dropped at the tile Katie faces when that is walkable, so the pile is not
    // under her feet and instantly picked back up; on her own tile otherwise,
    // which is always somewhere rather than nowhere.
    onDrop: (index: number) => {
      const slot = session.inventory.slotAt(index);
      if (!slot) return false;

      const facing = session.facingTile;
      const target = session.area.map.isSolidTile(facing.x, facing.y)
        ? { x: session.player.tileX, y: session.player.tileY }
        : facing;

      session.inventory.takeSlot(index);
      session.ground.drop(session.area.id, target.x, target.y, slot.id, slot.count);
      toast.show(
        slot.count > 1
          ? `Dropped ${slot.count} ${ITEMS[slot.id].name}`
          : `Dropped ${ITEMS[slot.id].name}`,
      );
      save();
      return true;
    },
  });
  disposers.push(() => { gearPanel.destroy(); });

  const shopPanel = new ShopPanel({
    container: uiLayer,
    inventory: session.inventory,
    wallet: session.wallet,
    // Prices soften as Pell gets to know you, which is what friendship buys.
    discount: () => session.friendships.tierFor('pell').discount,
    onChanged: () => { save(); },
    onMessage: (message: string) => { toast.show(message); },
  });
  disposers.push(() => { shopPanel.destroy(); });

  const boardPanel = new RequestBoardPanel({
    container: uiLayer,
    board: session.requests,
    inventory: session.inventory,
    dayIndex: () => session.clock.dayIndex,
    onFill: (request) => {
      const outcome = session.requests.fill(request.id, session.clock.dayIndex, {
        inventory: session.inventory,
        wallet: session.wallet,
        friendships: session.friendships,
      });

      if (outcome !== 'filled') {
        toast.show(
          outcome === 'short'
            ? `Not enough ${ITEMS[request.item].name}`
            : 'That notice has been taken down',
        );
        return;
      }

      toast.show(`${NPCS[request.from].name} pays ${request.pay}c`);
      save();
    },
  });
  disposers.push(() => { boardPanel.destroy(); });

  const dialogue = new DialoguePanel(uiLayer);
  disposers.push(() => { dialogue.destroy(); });

  const toolButton = new ToolButton({
    container: uiLayer,
    belt: session.belt,
    onChoose: (id: ItemId) => {
      session.belt.setActive(id);
      save();
    },
  });
  disposers.push(() => { toolButton.destroy(); });

  const forceTouch = new URLSearchParams(window.location.search).has('touch');
  const settings = loadSettings();

  try {
    if (forceTouch || TouchControls.isTouchDevice()) {
      document.body.classList.add('has-touch');
      const controls = new TouchControls({ container: uiLayer, slots: settings.slots });
      input.addSource(controls);
      disposers.push(() => { controls.destroy(); });

      const panel = new SettingsPanel({
        container: uiLayer,
        initial: settings,
        onChange: (next) => {
          controls.setSlots(next.slots);
          applyView(next.view);
          saveSettings(next);
        },
      });
      disposers.push(() => { panel.destroy(); });
    }
  } catch (error) {
    console.error('Touch controls failed to initialise.', error);
  }

  // Developer furniture, not part of the game. Shown in dev, and anywhere on
  // request via ?perf=1 — the case it exists for is reading the frame budget
  // on a real phone, which is a production build over the network.
  const showPerf = import.meta.env.DEV || params.has('perf');
  const perf = showPerf ? new PerfMonitor() : null;
  const perfHud = showPerf ? document.createElement('pre') : null;
  if (perfHud) {
    perfHud.className = 'perf-hud';
    uiLayer.appendChild(perfHud);
    disposers.push(() => { perfHud.remove(); });
  }

  const timestep = new FixedTimestep();
  let logicMs = 0;
  let logicEndedAt = 0;
  let sinceAutosave = 0;

  /** The swing in progress, if any. */
  const swing = new Swing();

  /** Everything currently trying to hurt Katie. */
  const enemies = new EnemyForce();

  const health = new Health(20);
  const spirit = new Spirit(30);
  const dodge = new Dodge();
  const combo = new Combo();
  const bolts = new Projectiles();

  /** Spirit spent per bolt. */
  const BOLT_COST = 8;

  /**
   * Seconds of frozen simulation after a hit lands.
   *
   * The single cheapest thing that makes a hit feel like contact rather than
   * an overlap: everything stops for a few frames, so the blow reads as having
   * weight. Rendering continues, so it looks deliberate rather than dropped.
   */
  let hitstop = 0;

  /** Damage of the swing in flight, fixed when the combo step was chosen. */
  let swingDamage = 0;

  /** And which weapon threw it, so the practice is credited to that blade. */
  let swingWeapon: WeaponId | null = null;

  /**
   * Who is spawning here, or null in a safe area.
   *
   * Rebuilt per area so a wave counter never carries between places, and so
   * walking out of danger genuinely leaves it behind.
   */
  let spawner: SpawnDirector | null = null;

  /**
   * Waves sent this session, across every area.
   *
   * Carried between directors so the first wave of a visit is never the first
   * wave of the last visit. Not saved: a reload is allowed to be a fresh
   * world, and enemies are not saved either.
   */
  let wavesSoFar = 0;

  /**
   * A number unique to this run of the game.
   *
   * Almost everything derived in this project is deterministic — the weather
   * from (seed, day), a dungeon floor from (seed, depth) — because those are
   * *places and conditions*, and being able to reproduce them is worth more
   * than surprise. A spawn wave is neither: it is an event, nobody needs to
   * return to it, and it is never saved.
   *
   * Determinism actively hurt here. Salting by the clock was not enough,
   * because a reload restores the same saved minute, so every session opened
   * with the same creature on the same tile — the fixed table this replaced,
   * one layer down.
   */
  const sessionNonce = randomSeed();

  /** Chests on the current floor, or null above ground. */
  let chests: Chests | null = null;

  /** And what is waiting in its corridors. */
  let traps: TrapField | null = null;

  /** The boss, on the floors that have one. */
  let boss: Boss | null = null;

  /** Its volleys. A separate flight from Katie's, aimed the other way. */
  const bossBolts = new Projectiles();

  const enterAreaSpawning = (): void => {
    enemies.clear();

    // Rebuilt from the same seed and depth the floor was, so the chests are
    // the floor's rather than something remembered beside it.
    bossBolts.clear();

    if (session.underground && isBossDepth(session.depth)) {
      // An arena has nothing in it but the fight.
      chests = null;
      traps = null;
      const definition = bossFor(session.depth);
      const centre = arenaCentre();
      boss = definition ? new Boss(definition, centre.x, centre.y) : null;
      if (boss) toast.show(boss.definition.name);
    } else if (session.underground) {
      // One floor, described once: chests and traps are two readings of the
      // same layout, so generating it twice would be two chances to disagree.
      const floor = generateFloor({ seed: session.seed, depth: session.depth });
      chests = Chests.forFloor(session.seed, floor);
      traps = TrapField.forFloor(session.seed, floor);
      boss = null;
    } else {
      chests = null;
      traps = null;
      boss = null;
    }
    if (spawner) wavesSoFar = spawner.wavesSent;

    // Nothing wanders into an arena. The comment above says a boss floor holds
    // only the fight, and leaving the spawner running quietly made that false —
    // grubs drifted in mid-fight because the area is still called 'dungeon'.
    const rules = isBossDepth(session.depth) ? undefined : AREA_SPAWN_RULES[session.area.id];
    if (!rules) {
      spawner = null;
      return;
    }
    // Salted by area so two places never share a wave sequence, and by the
    // session so no two runs open the same way. `wavesSoFar` covers walking
    // out and back within one session.
    spawner = new SpawnDirector(
      session.seed,
      hashArea(session.area.id) ^ sessionNonce,
      rules,
      wavesSoFar,
    );
    // Arriving somewhere dangerous should not be silent for twelve seconds.
    spawner.primeImmediately();
  };

  enterAreaSpawning();

  /** Where Katie was last checked for something underfoot. */
  let lastTileKey = '';

  /** Active cast, or null when not fishing. */
  let fishing: FishingSession | null = null;

  /** >0 fading out, <0 fading in, 0 idle. */
  let fadeDirection = 0;
  let fadeProgress = 0;
  let pendingTransition = false;

  const save = (): void => {
    void writeSave({
      version: CURRENT_SAVE_VERSION,
      player: {
        x: session.player.x,
        y: session.player.y,
        facing: session.player.facing,
      },
      map: session.area.id,
      totalMinutes: session.clock.totalMinutes,
      seed: session.seed,
      farmland: session.farmland.toSave(),
      inventory: session.inventory.toSave(),
      selectedSlot: session.quickBag.selectedIndex,
      quickBag: session.quickBag.toSave(),
      belt: session.belt.toSave(),
      livestock: session.livestock?.toSave() ?? [],
      furniture: session.furnitureToSave(),
      ground: session.ground.toSave(),
      weaponGrowth: session.weaponGrowth.toSave(),
      weaponAbilities: session.weaponAbilities.toSave(),
      coins: session.wallet.toSave(),
      friendships: session.friendships.toSave(),
      requests: session.requests.toSave(),
      quests: session.quests.toSave(),
      festivals: session.festivals.toSave(),
      depth: session.depth,
      surfaceReturn: session.surfaceReturnToSave(),
      savedAt: Date.now(),
    });
  };

  /**
   * Change how much world is on screen, without restarting anything.
   *
   * The renderer is resized and the camera re-clamped in the same breath: a
   * camera still clamped to the old view would sit against a bound that no
   * longer exists and show a strip of nothing at the edge of the map.
   */
  const applyView = (view: ViewName): void => {
    setView(view);
    applyViewport(app);
    camera.snapTo(session.player.centreX, session.player.centreY, session.area.bounds);
  };

  const enterArea = (): void => {
    tilemapRenderer.destroy();
    tilemapRenderer = new TilemapRenderer(session.area.map, tileTextures);
    layerWorld();
    enterAreaSpawning();
    camera.snapTo(session.player.centreX, session.player.centreY, session.area.bounds);
    save();
  };

  const simulate = (dt: number): void => {
    input.update();
    // Re-read after a press is consumed, so the rest of the step genuinely
    // does not see it.
    let state = input.state;

    // Movement is suppressed mid-fade so the player cannot walk back through a
    // door they are already halfway through, and while fishing so a cast is not
    // dragged across the pond.
    const canMove = fadeDirection === 0 && fishing === null;
    session.player.update(canMove ? state : NO_INPUT, session.area.map, dt);
    session.clock.advance(dt);
    camera.follow(
      session.player.centreX,
      session.player.centreY,
      session.area.bounds,
      dt,
    );

    session.livestockHere?.update(dt, session.area.map);

    // Picked up by walking onto the tile, and only on arrival — otherwise a
    // stack dropped where you stand is snatched straight back up.
    const tileKey = `${session.area.id}:${session.player.tileX},${session.player.tileY}`;
    if (tileKey !== lastTileKey) {
      lastTileKey = tileKey;
      const sprung = traps?.trigger(session.player.tileX, session.player.tileY) ?? 0;
      if (sprung > 0 && !dodge.isDodging && health.damage(sprung)) {
        toast.show(`${traps?.at(session.player.tileX, session.player.tileY)?.definition.name}! -${sprung}`);
      }

      const outcome = pickUpAt(
        session.ground,
        session.inventory,
        session.area.id,
        session.player.tileX,
        session.player.tileY,
      );
      const message = describePickup(outcome);
      if (message) toast.show(message);
      if (outcome.kind === 'picked') save();
    }

    // Any button dismisses the catch card early; it never blocks play. The
    // press is spent doing it, so the next one is what acts — otherwise
    // dismissing the card you just earned casts straight back into the water.
    if (catchPopup.isShowing && state.pressed.size > 0) {
      catchPopup.hide();
      input.consumePresses();
      state = input.state;
    }

    // The tackle box is a menu: while it is open the world keeps running but
    // Katie does not act, so a tap on a recipe cannot also swing a hoe.
    if (gearPanel.isOpen) {
      if (state.pressed.has('menu')) gearPanel.setOpen(false);
      return;
    }

    if (shopPanel.isOpen) {
      if (state.pressed.has('menu')) shopPanel.setOpen(false);
      return;
    }

    if (dialogue.isOpen) {
      if (state.pressed.has('menu') || state.pressed.has('interact')) dialogue.close();
      return;
    }

    if (fishing) {
      fishing.update(dt);
      if (state.pressed.has('interact')) fishing.strike();

      if (fishing.isOver) {
        const result = fishing.result;
        if (result?.fish) {
          const leftover = session.inventory.add(fishItem(result.fish.id as FishId), 1);
          if (leftover > 0) {
            toast.show('Your bag is full');
          } else {
            const previous = records.get(result.fish.id);
            const outcome = records.record(
              result.fish.id,
              result.inches,
              weightFor(result.fish, result.inches),
              Date.now(),
            );
            catchPopup.show(result.fish, result.inches, outcome, previous);
          }
        } else if (result) {
          toast.show(describeResult(result));
          // A failed cast is not nothing: the line comes back with something
          // snagged often enough that a bad session still feeds the workbench.
          if (result.reason === 'missed-strike' || result.reason === 'too-slow') {
            if (Math.random() < 0.35) {
              session.inventory.add('mat:driftwood', 1);
              toast.show('You reel in a piece of driftwood');
            }
          }
        }
        fishing = null;
        save();
      }
      return;
    }

    if (state.slotRequest !== null) session.quickBag.select(state.slotRequest);
    health.update(dt);
    spirit.update(dt);
    dodge.update(dt);
    combo.update(dt);
    swing.update(dt);

    // The roll moves her itself, outside the walk's acceleration curve.
    if (state.pressed.has('dash')) {
      dodge.start(state.move.x, state.move.y, session.player.facing);
    }
    if (dodge.isDodging) {
      const step = dodge.step(dt);
      session.player.displace(session.area.map, step.x, step.y);
    }

    if (state.pressed.has('special')) {
      if (spirit.spend(BOLT_COST)) {
        bolts.fire(
          session.player.centreX,
          session.player.centreY,
          session.player.facing,
          6,
        );
      } else {
        toast.show('Not enough spirit');
      }
    }

    bolts.update(dt, session.area.map, (box, damage) => {
      if (boss && !boss.isDead && boss.takeHit(box, damage, box.x, box.y)) {
        damageNumbers.spawn(damage, boss.x, boss.y - boss.definition.radius);
        if (boss.isDead) toast.show(`${boss.definition.name} falls — the way down opens`);
        return true;
      }

      const hits = enemies.resolveHit(box, damage, box.x, box.y);
      for (const hit of hits) {
        damageNumbers.spawn(hit.damage, hit.state.x, hit.state.y - 8);
        if (hit.killed) {
          toast.show(`${hit.state.definition.name} defeated`);
          dropSpoils(hit.state);
        }
      }
      return hits.length > 0;
    });

    // She fights on her own rhythm, and her kills drop what yours drop —
    // bringing a friend should not cost you the spoils.
    const struck = helper.update(dt, {
      playerX: session.player.x,
      playerY: session.player.y,
      enemies,
      minutes: session.clock.totalMinutes,
    });
    for (const state of struck.killed) {
      toast.show(`${state.definition.name} defeated`);
      dropSpoils(state);
    }
    if (!helper.isPresent && helperSprite.visible) {
      toast.show(`${NPCS[helper.id].name} heads home`);
      helperSprite.visible = false;
    }

    traps?.update(dt);

    if (boss && !boss.isDead) {
      const volley = boss.update(dt, session.player.centreX, session.player.centreY, session.area.map);
      if (volley) {
        // Spread around its facing, so a volley is something to move out of
        // rather than something to walk around.
        for (let i = 0; i < volley.count; i++) {
          bossBolts.fire(volley.x, volley.y, volley.facing, volley.damage);
        }
      }

      // Walking into it hurts, on the same i-frames as everything else.
      const reach = boss.definition.radius;
      const touching =
        Math.abs(boss.x - session.player.centreX) < reach + 6 &&
        Math.abs(boss.y - session.player.centreY) < reach + 6;
      if (touching && !dodge.isDodging && health.damage(boss.phase.contactDamage)) {
        toast.show(`Ouch! -${boss.phase.contactDamage}`);
      }
    }

    bossBolts.update(dt, session.area.map, (box, damage) => {
      const hit = {
        x: session.player.centreX - 6,
        y: session.player.centreY - 6,
        width: 12,
        height: 12,
      };
      if (!overlaps(box, hit)) return false;
      if (!dodge.isDodging) health.damage(damage);
      return true;
    });

    enemies.update(dt, session.player.centreX, session.player.centreY, session.area.map);

    // Waves arrive out of sight, in numbers that depend on the hour.
    for (const spawn of spawner?.update(dt, {
      map: session.area.map,
      playerX: session.player.centreX,
      playerY: session.player.centreY,
      isNight: isNightHour(session.clock.hour),
      population: enemies.count,
    }) ?? []) {
      enemies.spawn(spawn.id, spawn.x, spawn.y);
    }

    // Resolved every step the swing is live, not once when it starts: an
    // enemy walking into a swing already in flight has walked into a sword.
    if (swing.isSwinging) {
      const arc = swing.arcAt(session.player.centreX, session.player.centreY);
      const abilities = swingWeapon ? session.weaponAbilities.of(swingWeapon) : [];
      const hits = enemies.resolveHit(
        arc,
        swingDamage,
        session.player.centreX,
        session.player.centreY,
        swing.hits,
        abilities.includes('sapping'),
      );

      for (const hit of hits) {
        damageNumbers.spawn(hit.damage, hit.state.x, hit.state.y - 8);
        trainWeapon(hit.damage);
        if (abilities.includes('spiritDraw')) spirit.gain(SPIRIT_PER_HIT);
        if (hit.killed) {
          toast.show(`${hit.state.definition.name} defeated`);
          dropSpoils(hit.state);
        }
      }

      if (boss && !boss.isDead && !swing.hits.has(BOSS_HIT_ID)) {
        if (boss.takeHit(arc, swingDamage, session.player.centreX, session.player.centreY)) {
          swing.hits.add(BOSS_HIT_ID);
          trainWeapon(swingDamage);
          damageNumbers.spawn(swingDamage, boss.x, boss.y - boss.definition.radius);
          hitstop = 0.06;
          if (boss.isDead) {
            toast.show(`${boss.definition.name} falls — the way down opens`);
            save();
          }
        }
      }

      if (hits.length > 0) hitstop = 0.06;
    }

    const contact = enemies.contactDamage({
      x: session.player.centreX - 6,
      y: session.player.centreY - 6,
      width: 12,
      height: 12,
    });
    if (contact > 0 && !dodge.isDodging && health.damage(contact)) {
      toast.show(`Ouch! -${contact}`);
    }

    if (health.isDown) {
      // Waking up at home costs time and the walk back, not items. Losing a
      // bag of fish to a moth would teach players to never leave the farm.
      health.restore();
      spirit.restore();
      bolts.clear();
      session.sendToSpawn('cottage');
      enterArea();
      // Whoever was walking with you is not standing in your bedroom when you
      // come round. They got you home and went back to work.
      helper.dismiss();
      toast.show('You wake up back home');
    }

    // Attacking reaches for the weapon rather than requiring it to be in hand.
    // The alternative is a dedicated attack button that silently does nothing
    // while you are holding a hoe, which reads as broken — and pressing attack
    // is an unambiguous statement about what you want to be holding.
    if (state.pressed.has('attack')) {
      const inHand = session.belt.activeTool;
      const attack = beginAttack({
        belt: session.belt,
        swing,
        combo,
        facing: session.player.facing,
        level:
          inHand && isWeaponId(inHand.id)
            ? session.weaponGrowth.levelFor(inHand.id)
            : session.weaponGrowth.levelFor(
                (session.belt.best('sword')?.id ?? 'pruningBlade') as WeaponId,
              ),
      });
      if (attack.started) {
        swingDamage = attack.damage;
        swingWeapon = attack.weapon;
      }
    }

    if (state.pressed.has('nextTool')) session.belt.cycle();
    if (state.pressed.has('nextItem')) session.quickBag.cycle(1);
    if (state.pressed.has('prevItem')) session.quickBag.cycle(-1);

    if (fadeDirection === 0) {
      if (session.tryTransitionPending()) {
        fadeDirection = 1;
        fadeProgress = 0;
        pendingTransition = true;
      } else if (state.pressed.has('interact')) {
        // Sleeping wins over farming when stood by the bed; there is nothing to
        // farm in a bedroom, so the two never genuinely compete.
        const facing = session.facingTile;

        const person = npcs.at(
          session.area.id,
          session.clock.hour,
          facing.x,
          facing.y,
          festivalToday(),
        );
        if (person) {
          // Turning up counts, once a day.
          session.friendships.greet(person.id, session.clock.dayIndex);
          save();

          const options = [{ label: 'Goodbye', onChoose: () => { dialogue.close(); } }];

          if (session.friendships.canReceiveGift(person.id, session.clock.dayIndex)) {
            options.unshift({
              label: 'Give something',
              onChoose: () => { offerGift(person); },
            });
          }

          // The festival is the reason everyone is here, so it leads.
          const festival = festivalToday();
          if (
            festival &&
            festival.host === person.id &&
            !session.festivals.hasEntered(festival, session.clock.year)
          ) {
            options.unshift({
              label: `Enter the ${festival.name}`,
              onChoose: () => { enterFestival(person, festival); },
            });
          }

          // Someone who has trusted you with her father's work will walk with
          // you for the rest of the day. Offered only when she is out and
          // about: she cannot come along from a place she is not.
          if (
            session.quests.isComplete('mira-blade') &&
            person.id === 'mira' &&
            !helper.isPresent
          ) {
            options.unshift({
              label: 'Walk with me a while',
              onChoose: () => {
                const spot = summonPosition(session.player.x, session.player.y);
                helper.summon(person.id, spot.x, spot.y, homeTimeFor(session.clock.totalMinutes));
                dialogue.show(
                  person.definition.name,
                  'Aye, go on. The fire will keep until dark — but not past it.',
                  [{ label: 'Goodbye', onChoose: () => { dialogue.close(); } }],
                );
              },
            });
          }

          // Whatever they have asked of you comes first: a person mid-way
          // through asking for something should not lead with small talk.
          for (const quest of questsFrom(person.id)) {
            const progress = session.quests.progress(quest.id, session);
            if (progress.state === 'unavailable' || progress.state === 'complete') continue;

            options.unshift(
              progress.state === 'offered'
                ? {
                    label: 'What are you working on?',
                    onChoose: () => { offerQuest(person, quest.id); },
                  }
                : {
                    label: progress.canHandIn ? 'I have what you wanted' : 'About that job',
                    onChoose: () => { showQuest(person, quest.id); },
                  },
            );
          }

          if (person.definition.trades) {
            options.unshift({
              label: 'Trade',
              onChoose: () => {
                dialogue.close();
                shopPanel.setOpen(true);
              },
            });
          }
          dialogue.show(
            person.definition.name,
            npcs.speak(person.definition, {
              hour: session.clock.hour,
              season: session.clock.season,
              weather: session.weather,
              festival: festivalToday()?.id ?? null,
            }),
            options,
          );
          return;
        }

        if (session.area.map.at(facing.x, facing.y) === 'N') {
          // Readable at any hour. Nobody has to be standing at a board for
          // the paper on it to say what it says.
          boardPanel.setOpen(true);
          return;
        }

        if (session.area.map.at(facing.x, facing.y) === 'K') {
          // The cart is his, not furniture. With Pell packed up and gone, it
          // is a shuttered cart — which is the whole reason he has a schedule.
          if (npcs.isAbout('pell', session.area.id, session.clock.hour, festivalToday())) {
            shopPanel.setOpen(true);
          } else {
            toast.show('The cart is shuttered. Pell trades from eight until dusk.');
          }
          return;
        }

        const chest =
          chests?.at(facing.x, facing.y) ??
          chests?.at(session.player.tileX, session.player.tileY) ??
          null;

        const underfoot = session.area.map.at(session.player.tileX, session.player.tileY);

        if (chest && !chest.opened) {
          const haul = chests?.open(chest.x, chest.y) ?? [];
          for (const stack of haul) {
            if (isTool(stack.item)) {
              // Weapons and tools live on the belt, which retires whatever
              // they replace. Handing one to the backpack would lose it.
              const outcome = session.belt.acquire(stack.item);
              toast.show(
                outcome
                  ? `${ITEMS[stack.item].name} — a better one than yours`
                  : `${ITEMS[stack.item].name}, but yours is better`,
              );
              continue;
            }

            const leftover = session.inventory.add(stack.item, stack.count);
            if (leftover > 0) {
              // Spilled rather than lost: the floor is somewhere you can come
              // back to, and a full backpack should never eat a reward.
              session.ground.drop(session.area.id, chest.x, chest.y, stack.item, leftover);
              toast.show(`${ITEMS[stack.item].name} — no room, some spilled`);
            } else {
              toast.show(`${stack.count} ${ITEMS[stack.item].name}`);
            }
          }
          if (haul.length === 0) toast.show('Empty');
          save();
        } else if (underfoot === '>' || underfoot === '<') {
          // Used rather than triggered by standing: falling a floor because you
          // walked over a hole is the kind of thing that loses a run.
          if (underfoot === '>') {
            if (boss && !boss.isDead) {
              toast.show(`${boss.definition.name} bars the way`);
            } else if (session.depth >= MAX_DEPTH) {
              toast.show('The stair is blocked by rubble');
            } else {
              session.descend();
              enterArea();
              toast.show(session.area.name);
            }
          } else {
            session.ascend();
            enterArea();
            toast.show(session.underground ? session.area.name : 'Back in the daylight');
          }
        } else if (session.canSleep()) {
          session.sleep();
          const days = session.syncGrowth();
          toast.show(days > 0 ? 'A new day begins' : 'You rest a while');
          save();
        } else {
          const target = session.facingTile;
          const outcome = useHeldItem({
            map: session.area.map,
            farmland: session.farmlandHere,
            inventory: session.inventory,
            quickBag: session.quickBag,
            season: session.clock.season,
            dayIndex: session.clock.dayIndex,
            x: target.x,
            y: target.y,
            furniture: session.furniture,
            livestock: session.livestockHere,
            activeTool: session.belt.activeId,
          });

          if (outcome.kind === 'fish') {
            const rod = isRodId(outcome.rod) ? RODS[outcome.rod] : RODS.bambooRod;
            const baitItem = session.equippedBait as ItemId | null;
            const bait = baitItem ? baitOf(baitItem) : null;

            // Bait is spent on the cast, not the catch: the fish took it
            // either way. Lures survive, which is what they are for.
            if (baitItem && bait && !BAITS[bait].reusable) {
              session.inventory.remove(baitItem, 1);
            }

            fishing = new FishingSession({
              water: outcome.water,
              season: session.clock.season,
              hour: session.clock.hour,
              rodTier: rod.tier,
              bait,
            });
          } else {
            announce(outcome);
          }
        }
      }
    }

    if (fadeDirection !== 0) {
      fadeProgress += dt / FADE_SECONDS;
      if (fadeProgress >= 1) {
        fadeProgress = 0;
        if (fadeDirection === 1 && pendingTransition) {
          pendingTransition = false;
          session.commitTransition();
          enterArea();
          fadeDirection = -1;
        } else {
          fadeDirection = 0;
        }
      }
    }
  };

  app.ticker.add(
    (ticker) => {
      const start = performance.now();

      const steps = timestep.advance(ticker.deltaMS);
      for (let i = 0; i < steps; i++) {
        if (hitstop > 0) {
          hitstop -= timestep.stepSeconds;
          continue;
        }
        simulate(timestep.stepSeconds);
      }

      const alpha = timestep.alpha;
      const player = session.player;
      const drawX = Math.round(interpolate(player.previousX, player.x, alpha));
      const drawY = Math.round(interpolate(player.previousY, player.y, alpha));

      katie.texture = katieTextures[spriteKeyFor(player.facing, player.frame)];
      katie.scale.x = player.facing === 'left' ? -1 : 1;

      // The tool tracks Katie exactly, flip included, because the overlay is
      // authored on her own 16x16 grid. Facing away puts it behind her so her
      // body occludes it instead of the tool sitting on her shoulders.
      const activeTool = session.belt.activeTool;
      const heldSprite = player.facing === 'up' ? heldToolBehind : heldToolFront;
      const idleSprite = player.facing === 'up' ? heldToolFront : heldToolBehind;
      idleSprite.visible = false;
      // Shown while fishing too: the rod is the one thing she is unambiguously
      // holding at that moment.
      // A swing moves three things: the blade travels, the body leans into it,
      // and the arc is the size of the weapon's reach. Before this, none of
      // them moved — a static blade beside a static Katie with a white arc
      // blinking in front, identical for every weapon in the game.
      const pose = swing.isSwinging ? swingPose(swing.progress, swing.reachTiles) : REST_POSE;
      const lunge = SWING_OFFSETS[swing.isSwinging ? swing.facing : player.facing];

      const bodyX = drawX + lunge.x * pose.lunge;
      const bodyY = drawY + lunge.y * pose.lunge;
      // Facing left mirrors her by scaling x negative, which pivots about the
      // left edge — so the draw position shifts a whole sprite right to put
      // her back where she stands.
      katie.position.set(
        player.facing === 'left' ? bodyX + katie.texture.width : bodyX,
        bodyY,
      );

      const centreX = bodyX + katie.texture.width / 2;
      const centreY = bodyY + katie.texture.height / 2;

      heldSprite.visible = activeTool !== undefined;
      if (heldSprite.visible && activeTool) {
        heldSprite.texture = heldToolTextures[heldToolSpriteKey(activeTool.family, player.facing)];
        heldSprite.scale.x = katie.scale.x;
        // Anchored centre so rotation pivots through her hands rather than
        // swinging the whole sprite around its top-left corner.
        heldSprite.anchor.set(0.5);
        heldSprite.rotation = pose.toolAngle;
        heldSprite.position.set(
          centreX + lunge.x * pose.toolForward,
          centreY + lunge.y * pose.toolForward,
        );
      }

      slash.visible = swing.isSwinging;
      if (swing.isSwinging) {
        slash.texture = slashTextures[SLASH_FRAMES[swing.frameIndex(SLASH_FRAMES.length)]!];
        const direction = SWING_OFFSETS[swing.facing];
        slash.scale.set(pose.arcScale);
        slash.position.set(
          centreX + direction.x * SWING_REACH_PX * pose.arcScale,
          centreY + direction.y * SWING_REACH_PX * pose.arcScale,
        );
        slash.rotation = direction.rotation;
      }

      const cameraX = Math.round(camera.x);
      const cameraY = Math.round(camera.y);
      tilemapRenderer.render(cameraX, cameraY);
      // Only where crops belong. The plot layer is the farm's, and drawing it
      // everywhere put a planted field on every floor of the dungeon.
      farmlandRenderer.container.visible = session.farmlandHere !== null;
      if (session.farmlandHere) {
        farmlandRenderer.render(cameraX, cameraY, session.clock.dayIndex);
      }
      propsRenderer.render({
        cameraX,
        cameraY,
        furniture: session.furniture,
        livestock: session.livestockHere,
        trough: session.area.definition.trough,
        dt: ticker.deltaMS / 1000,
      });
      groundItemsRenderer.render(session.ground.in(session.area.id), cameraX, cameraY);
      enemyRenderer.render(
        enemies.all().map(([, state]) => state),
        enemies.frame,
      );
      const flying = [...bolts.all(), ...bossBolts.all()];
      for (let i = 0; i < flying.length; i++) {
        let sprite = boltSprites[i];
        if (!sprite) {
          sprite = new Sprite(boltTexture);
          sprite.anchor.set(0.5);
          boltSprites.push(sprite);
          boltLayer.addChild(sprite);
        }
        sprite.position.set(Math.round(flying[i]!.x), Math.round(flying[i]!.y));
        sprite.visible = true;
      }
      for (let i = flying.length; i < boltSprites.length; i++) boltSprites[i]!.visible = false;

      bossSprite.visible = boss !== null && !boss.isDead;
      if (boss && !boss.isDead) {
        bossSprite.texture = bossTextures[boss.definition.id];
        bossSprite.position.set(Math.round(boss.x), Math.round(boss.y));
        bossSprite.tint = boss.flash > 0 ? 0xffffff : 0xd8d8d8;
      }

      trapRenderer.render(traps?.all() ?? []);
      npcRenderer.render(npcs.in(session.area.id, session.clock.hour, festivalToday()));
      helperSprite.visible = helper.isPresent;
      if (helper.isPresent) {
        helperSprite.texture = npcTextures[helper.id];
        helperSprite.position.set(Math.round(helper.x - 8), Math.round(helper.y - 12));
      }
      chestRenderer.render(chests?.all() ?? []);
      damageNumbers.update(ticker.deltaMS / 1000);
      // Blink while safe, so the player can see the window they were given.
      katie.alpha = health.isInvulnerable && Math.floor(health.invulnerableRemaining * 12) % 2 === 0
        ? 0.4
        : 1;
      world.position.set(-cameraX, -cameraY);

      // Only aim when there is something to aim: an empty hand acts on nothing,
      // and a cursor pointing at a tile that will not respond is a lie.
      const target = session.facingTile;
      tileCursor.update(
        target.x,
        target.y,
        cameraX,
        cameraY,
        fishing === null &&
          fadeDirection === 0 &&
          !gearPanel.isOpen &&
          !catchPopup.isShowing &&
          (session.belt.activeId !== null ||
            isPlaceable(session.quickBag.heldItem(session.inventory))),
      );

      const ambient = ambientFor({
        minuteOfDay: session.clock.minuteOfDay,
        weather: session.weather,
        indoor: session.indoor,
      });
      ambientOverlay.tint = ambient.colour;
      ambientOverlay.alpha = ambient.alpha;

      weatherRenderer.update(
        session.indoor ? 'clear' : session.weather,
        ticker.deltaMS / 1000,
      );

      fadeOverlay.alpha = fadeDirection === 1 ? fadeProgress : fadeDirection === -1 ? 1 - fadeProgress : 0;

      healthBar.update(health.current, health.max);
      spiritBar.style.setProperty('--fill', String(spirit.current / spirit.max));

      bossBar.hidden = boss === null || boss.isDead;
      if (boss && !boss.isDead) {
        bossFill.style.setProperty('--fill', String(boss.fraction));
        // The phase is named, because "it got faster" should be something the
        // player is told rather than something they suspect.
        bossName.textContent = `${boss.definition.name} — phase ${boss.phaseIndex}`;
      }
      itemBar.update(session.quickBag, session.inventory);
      // Both live in the top-right corner, so one covering the other would be
      // the picker hanging over an open gear screen.
      if (gearPanel.isOpen || shopPanel.isOpen || dialogue.isOpen) toolButton.setOpen(false);
      toolButton.update(session.belt);
      fishingUi.update(fishing);
      hud.update({
        hour: session.clock.hour,
        minute: session.clock.minute,
        season: session.clock.season,
        dayOfSeason: session.clock.dayOfSeason,
        weather: session.weather,
        areaName: session.area.name,
        festival: festivalToday()?.blurb ?? null,
      });

      sinceAutosave += ticker.deltaMS;
      if (sinceAutosave >= AUTOSAVE_INTERVAL_MS) {
        sinceAutosave = 0;
        save();
      }

      logicEndedAt = performance.now();
      logicMs = logicEndedAt - start;
    },
    undefined,
    UPDATE_PRIORITY.HIGH,
  );

  if (perf && perfHud) {
    app.ticker.add(
      (ticker) => {
        perf.record(ticker.deltaMS, logicMs, performance.now() - logicEndedAt);
        perfHud.textContent = formatPerf(perf.snapshot());
      },
      undefined,
      UPDATE_PRIORITY.UTILITY,
    );
  }

  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.hidden) {
        app.ticker.stop();
        save();
      } else {
        timestep.reset();
        app.ticker.start();
      }
    },
    { signal },
  );

  window.addEventListener('pagehide', save, { signal });

  // Dev-only inspection hook.
  //
  // Several bugs in this project existed only in the *composition* of files
  // that each read correctly, and were found by driving the running game. A
  // swing lasts under a third of a second, which no screenshot round-trip can
  // catch, so the state has to be readable directly. The guard below strips
  // this from production builds.
  if (import.meta.env.DEV) {
    (window as unknown as { __heartland?: unknown }).__heartland = {
      session,
      app,
      swing,
      helper,
      catchPopup,
      isFishing: () => fishing !== null,
      enemies,
      health,
      spirit,
      dodge,
      combo,
      bolts,
      chests: () => chests,
      traps: () => traps,
      boss: () => boss,
      npcs,
      isSlashDrawn: () => slash.visible,
    };
  }

  return teardown;

  /**
   * Credit damage dealt back to the blade that dealt it.
   *
   * Experience is damage, so there is nothing to grind at: training a weapon
   * and fighting with it are the same activity.
   */
  function trainWeapon(damage: number): void {
    if (!swingWeapon) return;
    const reached = session.weaponGrowth.gain(swingWeapon, damage);
    if (reached !== null) {
      toast.show(`${ITEMS[swingWeapon].name} reaches level ${reached}`);
      save();
    }
  }

  /**
   * Leave what a defeated thing was made of on the floor.
   *
   * Dropped rather than granted, so a kill in a corner is something you choose
   * to go and collect — and so a full backpack never silently eats it.
   */
  function dropSpoils(state: { id: string; x: number; y: number; definition: { drop: { item: ItemId; chance: number } } }): void {
    const { drop } = state.definition;
    if (Math.random() >= drop.chance) return;
    session.ground.drop(
      session.area.id,
      Math.floor(state.x / 16),
      Math.floor(state.y / 16),
      drop.item,
      1,
    );
  }

  /**
   * Hand something to the judges.
   *
   * Whatever you are carrying that fits, and the best of it — a festival is
   * not a place to make you open a menu and choose between two turnips.
   */
  function enterFestival(
    person: { id: NpcId; definition: NpcDefinition },
    festival: Festival,
  ): void {
    const outcome = session.festivals.enter(festival, {
      inventory: session.inventory,
      wallet: session.wallet,
      friendships: session.friendships,
      hour: session.clock.hour,
      year: session.clock.year,
    });

    if (!outcome) {
      dialogue.show(person.definition.name, festival.empty, [
        { label: 'Goodbye', onChoose: () => { dialogue.close(); } },
      ]);
      return;
    }

    save();

    const name = ITEMS[outcome.item].name;
    dialogue.show(
      person.definition.name,
      outcome.ribbon
        ? `${name}! That takes the ribbon, and no argument from anyone.`
        : `${name}. Well brought. Set it down over there.`,
      [{ label: 'Goodbye', onChoose: () => { dialogue.close(); } }],
    );
    toast.show(`${festival.name} — ${outcome.coins}c`);
  }

  /**
   * She explains what she is at, and you say yes or you do not.
   *
   * Taking the job is a decision rather than a side effect of talking, so a
   * conversation can never quietly commit you to fetching things.
   */
  function offerQuest(person: { id: NpcId; definition: NpcDefinition }, id: QuestId): void {
    const progress = session.quests.progress(id, session);
    if (!progress.stage) return;

    dialogue.show(person.definition.name, progress.stage.ask, [
      {
        label: 'I will help',
        onChoose: () => {
          session.quests.start(id);
          save();
          showQuest(person, id);
        },
      },
      { label: 'Not just now', onChoose: () => { dialogue.close(); } },
    ]);
  }

  /** Where the job stands, and the chance to hand over what it wants. */
  function showQuest(person: { id: NpcId; definition: NpcDefinition }, id: QuestId): void {
    const progress = session.quests.progress(id, session);
    if (!progress.stage) {
      dialogue.show(person.definition.name, progress.quest.afterword, [
        { label: 'Goodbye', onChoose: () => { dialogue.close(); } },
      ]);
      return;
    }

    const { stage, carrying } = progress;
    const options = [{ label: 'Goodbye', onChoose: () => { dialogue.close(); } }];

    if (progress.canHandIn) {
      options.unshift({
        label: `Hand over ${stage.count} ${ITEMS[stage.item].name}`,
        onChoose: () => {
          const outcome = session.quests.handIn(id, session);
          if (!outcome) return;
          save();

          dialogue.show(person.definition.name, outcome.line, [
            { label: 'Goodbye', onChoose: () => { dialogue.close(); } },
          ]);

          if (outcome.finished) {
            toast.show(`${progress.quest.name} — ${progress.quest.reward.coins}c, and a pattern`);
          }
        },
      });
    }

    dialogue.show(
      person.definition.name,
      // The count is the whole status: what is wanted, and how far off it is.
      `${stage.ask} (${carrying} / ${stage.count})`,
      options,
    );
  }

  /**
   * Offer something from the backpack.
   *
   * Tools are absent on purpose: a merchant who accepts your hoe as a present
   * is a merchant who has taken your hoe. Only things you gathered or grew.
   */
  function offerGift(person: { id: NpcId; definition: NpcDefinition }): void {
    const seen = new Set<ItemId>();
    const offers: { label: string; onChoose: () => void }[] = [];

    for (let i = 0; i < session.inventory.capacity; i++) {
      const slot = session.inventory.slotAt(i);
      if (!slot || seen.has(slot.id)) continue;
      if (isTool(slot.id)) continue;
      seen.add(slot.id);

      const item = slot.id;
      offers.push({
        label: ITEMS[item].name,
        onChoose: () => {
          if (session.inventory.remove(item, 1) !== 1) return;

          const outcome = session.friendships.give(
            person.definition,
            item,
            session.clock.dayIndex,
          );
          save();

          dialogue.show(person.definition.name, outcome.line, [
            { label: 'Goodbye', onChoose: () => { dialogue.close(); } },
          ]);

          if (outcome.promotedTo) {
            toast.show(`${person.definition.name} — ${outcome.promotedTo}`);
          }
        },
      });
    }

    if (offers.length === 0) {
      dialogue.show(person.definition.name, 'You have nothing to spare just now.', [
        { label: 'Goodbye', onChoose: () => { dialogue.close(); } },
      ]);
      return;
    }

    offers.push({ label: 'Never mind', onChoose: () => { dialogue.close(); } });
    dialogue.show(person.definition.name, 'Something for me?', offers);
  }

  /** Turn a farming outcome into something the player can read. */
  function announce(outcome: ReturnType<typeof useHeldItem>): void {
    switch (outcome.kind) {
      case 'tilled':
      case 'watered':
        // Both are visible in the world itself; a message would be noise.
        break;
      case 'planted':
        toast.show(`Planted ${outcome.cropId}`);
        break;
      case 'harvested':
        toast.show(
          outcome.overflowed
            ? 'Your bag is full'
            : outcome.tier === 'rich'
              ? `Harvested ${outcome.amount} ${outcome.cropId} — rich soil`
              : `Harvested ${outcome.amount} ${outcome.cropId}`,
        );
        break;
      case 'fed':
        toast.show(`${outcome.name} is fed`);
        break;
      case 'collected':
        toast.show(outcome.overflowed ? 'Your bag is full' : `Collected an egg`);
        break;
      case 'placed':
      case 'retrieved':
        // Visible in the room itself.
        break;
      case 'fish':
        break;
      case 'gathered':
        toast.show(`Found ${outcome.item.split(':').pop() ?? 'something'}`);
        break;
      case 'nothing':
        toast.show(outcome.reason);
        break;
    }
  }

  function teardown(): void {
    lifetime.abort();
    for (const dispose of disposers) {
      try {
        dispose();
      } catch (error) {
        console.warn('Teardown step failed.', error);
      }
    }
    app.destroy(false, { children: true });
    document.body.classList.remove('has-touch');
  }
}

/** Where the arc is drawn, and the quarter turn that points it that way. */
const SWING_REACH_PX = 12;
const SWING_OFFSETS = {
  down: { x: 0, y: 1, rotation: Math.PI / 2 },
  up: { x: 0, y: -1, rotation: -Math.PI / 2 },
  left: { x: -1, y: 0, rotation: Math.PI },
  right: { x: 1, y: 0, rotation: 0 },
} as const;

const NO_INPUT = {
  move: { x: 0, y: 0 },
  held: new Set<never>(),
  pressed: new Set<never>(),
  released: new Set<never>(),
  slotRequest: null,
};

function applyViewport(app: Application): void {
  // Shape the view to this screen first: the canvas then covers it, rather
  // than sitting inside black bars on anything that is not 16:9.
  fitViewTo(window.innerWidth, window.innerHeight);

  const viewport = computeViewport({
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
  });

  app.renderer.resolution = viewport.resolution;
  app.renderer.resize(viewWidth(), viewHeight());
  canvas.style.width = `${viewport.cssWidth}px`;
  canvas.style.height = `${viewport.cssHeight}px`;

  // Publish where the picture actually is. On a phone wider than 16:9 the
  // canvas is letterboxed, and the HUD was anchored to the window — so the
  // clock, the hearts and the top-right buttons sat out on the background
  // beside the game. Input surfaces deliberately keep the whole window: a
  // thumb resting past the edge of the picture should still steer.
  const root = document.documentElement.style;
  root.setProperty('--stage-left', `${Math.max(0, viewport.offsetX)}px`);
  root.setProperty('--stage-top', `${Math.max(0, viewport.offsetY)}px`);
  root.setProperty('--stage-right', `${Math.max(0, window.innerWidth - viewport.offsetX - viewport.cssWidth)}px`);
  root.setProperty('--stage-bottom', `${Math.max(0, window.innerHeight - viewport.offsetY - viewport.cssHeight)}px`);

  rotatePrompt.hidden = !shouldPromptRotate(window.innerWidth, window.innerHeight);
}

/** Stable per-area number, so an area's wave sequence is its own. */
function hashArea(areaId: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < areaId.length; i++) {
    hash = Math.imul(hash ^ areaId.charCodeAt(i), 0x01000193);
  }
  return hash >>> 0;
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing required element: ${selector}`);
  return element;
}

// No import.meta.hot handling on purpose: as the entry module accepting
// nothing, any change triggers a full reload, which is the only way to
// guarantee no stale GPU resources or duplicate tickers.
void boot();
